<template>
  <q-tab-pane name="general">
    <!-- User avatar -->
    <div class="row gutter-x-xl">
      <div class="col-12 col-md-3">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('avatar') }}</h6>

        <p class="text-faded">{{ $t('image_hint') }}</p>
      </div>

      <div class="col-12 col-md-9">
        <image-input
          :uploadEndpoint="uploadAvatar"
          :deleteEndpoint="removeAvatar"
          :imageUrl="userAvatar"
          :imageType="$t('avatar')"
        >
          <user-avatar slot="placeholder" :user="user" class="q-display-3" height="160px" width="160px"/>
        </image-input>
      </div>
    </div>

    <hr/>

    <div class="row gutter-x-xl">
      <div class="col-12 col-md-3">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('account') }}</h6>

        <i18n tag="p" class="text-faded" path="account_hint">
          <a place="securitySettings" class="primary-link" href="#" @click.prevent="$emit('selectTab', 'security')">
            {{ $t('security_settings') }}
          </a>
        </i18n>
      </div>

      <div class="col-12 col-md-9">
        <!-- Settings form -->
        <form class="settings-form" @submit.prevent="handleSaveChanges">
          <!-- First name -->
          <q-field
            :error="errors.has('firstName')"
            :error-label="errors.first('firstName')"
          >
            <q-input
              v-model="userData.data.first_name"
              v-validate.disable="'required|min:1|max:255'"
              name="firstName"
              :float-label="$t('first_name')"
              required/>
          </q-field>

          <!-- Last name -->
          <q-field
            :error="errors.has('lastName')"
            :error-label="errors.first('lastName')"
          >
            <q-input
              v-model="userData.data.last_name"
              v-validate.disable="'required|min:1|max:255'"
              name="lastName"
              :float-label="$t('last_name')"
              required/>
          </q-field>

          <!-- Email address -->
          <q-field
            :error="errors.has('email')"
            :error-label="errors.first('email')"
          >
            <q-input
              v-model.trim="userData.data.email"
              v-validate.disable="'required|email|max:255'"
              type="email"
              name="email"
              :float-label="$t('email')"
              required/>
          </q-field>

          <!-- Language -->
          <language-input v-model="userData.data.settings.language"/>

          <div class="row justify-end">
            <q-btn type="submit" color="primary">{{ $t('save') }}</q-btn>
          </div>
        </form>
      </div>
    </div>

    <authorization-dialog ref="authorizationDialog"></authorization-dialog>
  </q-tab-pane>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import AuthorizationDialog from 'components/Common/AuthorizationDialog.vue'
  import ImageInput from 'components/Common/Inputs/ImageInput.vue'
  import LanguageInput from 'components/Common/Inputs/LanguageInput.vue'
  import UserAvatar from 'components/Common/UserAvatar.vue'
  import VuexDataBindingHelper from 'src/utils/VuexDataBindingHelper'
  import { createErrorFromResponse } from 'src/utils/error'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        userData: null
      }
    },

    computed: {
      ...mapGetters(['user']),

      userAvatar () {
        return this.user ? this.user.avatar_url : null
      }
    },

    methods: {
      ...mapActions(['updateUser', 'uploadAvatar', 'removeAvatar']),

      handleSaveChanges () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            if (this.userData.isDirty('email')) {
              return this.$refs.authorizationDialog.open()
                .then(authorizationPassword => {
                  this.saveChanges(authorizationPassword)
                })
            }

            this.saveChanges()
          })
      },

      saveChanges (authorizationPassword = null) {
        let data = this.userData.getChangedData()

        if (authorizationPassword) {
          data.confirmation_password = authorizationPassword
        }

        this.updateUser(data)
          .then(() => {
            this.$q.notify({
              message: this.userData.isDirty('email') ? this.$t('settings_save_success_email') : this.$t('settings_save_success'),
              timeout: 7500,
              type: 'positive'
            })

            this.initData()
          })
          .catch(createErrorFromResponse)
      },

      initData () {
        this.userData = new VuexDataBindingHelper(
          this.user,
          ['first_name', 'last_name', 'email', { key: 'settings.language', as: 'language' }]
        )
      }
    },

    created () {
      this.initData()
    },

    components: {
      AuthorizationDialog,
      ImageInput,
      LanguageInput,
      UserAvatar
    }
  }
</script>
